import React, { useContext, useState, useEffect, useId, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import { validateCedula } from "../../../../helpers/Helpers";
import { getToken } from "../../../../api/Account";
import { DataContext } from "../../../../context/DataContext";
import { tienePermiso } from "../../../../helpers/permisos";
import {
  getEmployeeByIdAsync,
  getPuestosAsync,
  updateEmployeeAsync,
} from "../../../../api/Employees";
import {
  EstadoError,
  SeccionFormulario,
  useAviso,
} from "../../../../components/base";
import MediumModal from "../../../../components/modals/MediumModal";
import ChangeSalario from "./ChangeSalario";
import LiquidateEmployee from "./LiquidateEmployee";
import PrintLiquidacion from "../liquidacion/PrintLiquidacion";
import {
  CampoFecha,
  CargandoFormularioEmpleado,
  DialogoFormulario,
  llevarAlPrimerError,
  useCargaLocal,
  useEnvio,
  useEscritura,
} from "../comunes";

/**
 * Reglas de validación de la ficha, las mismas de siempre y en el mismo orden que antes.
 * Ojo: la del puesto compara `selectedPuesto.length`, que en un número es `undefined`, así que
 * nunca falla (B18, se propone aparte): se deja tal cual.
 */
export const validarFicha = ({
  firstName,
  lastName,
  identification,
  selectedPuesto,
  address,
}) => {
  const errores = {};
  if (firstName.length === 0) errores.firstName = "Ingrese el primer nombre";
  if (lastName.length === 0) errores.lastName = "Ingrese el primer apellido";
  if (identification.length === 0) {
    errores.identification = "Ingrese la cédula";
  } else if (!validateCedula(identification)) {
    errores.identification = "La cédula no es válida. Formato: 000-000000-0000X";
  }
  if (selectedPuesto.length === 0) errores.selectedPuesto = "Seleccione un puesto";
  if (address.length === 0) errores.address = "Ingrese la dirección";
  return errores;
};

/**
 * Pestaña "Empleado": la ficha, con sus acciones a la vista (Editar, Cambiar salario y Liquidar,
 * o Imprimir liquidación si ya está inactivo). Lo que carga lo comunica al encabezado con `onDatos`.
 */
const EmployeeDetails = ({ id, onDatos, onFallo, isActive, setIsActive }) => {
  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  // Cada acción pide el permiso que exige su endpoint (EmployeesController): editar es UpdateEmployee y
  // cambiar el salario es ChangeSalario (NOMINA UPDATE); liquidar es LiquidarEmpleado (NOMINA CREAR).
  const puedeEditar = tienePermiso(access, "NOMINA UPDATE");
  const puedeLiquidar = tienePermiso(access, "NOMINA CREAR");
  const prefijo = useId();
  const formulario = useRef(null);

  const [dataEmployee, setDataEmployee] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [secondName, setSecondName] = useState("");
  const [lastName, setLastName] = useState("");
  const [secondLastName, setSecondLastName] = useState("");
  const [identification, setIdentification] = useState("");

  const [puestoList, setPuestoList] = useState([]);
  const [selectedPuesto, setSelectedPuesto] = useState("");

  const [inssNumber, setInssNumber] = useState("");
  const [address, setAddress] = useState("");
  const [tipoContrato, setTipoContrato] = useState(0);
  const [fechaInicio, setFechaInicio] = useState(new Date());

  const [isEdit, setIsEdit] = useState(false);
  const [showEditSalario, setEditSalario] = useState(false);
  const [localReload, setLocalReload] = useState(false);

  const [showModalLiaquidar, setModalLiaquidar] = useState(false);
  const [showModalLiaquidarPrint, setModalLiaquidarPrint] = useState(false);

  // Hay un bono escrito en la liquidación: un clic fuera no debe perderlo.
  const [liquidacionEscrita, setLiquidacionEscrita] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const errores = intentado
    ? validarFicha({ firstName, lastName, identification, selectedPuesto, address })
    : {};

  const cargaPuestos = useCargaLocal(() => getPuestosAsync(token));
  const cargaEmpleado = useCargaLocal(() => getEmployeeByIdAsync(token, id));
  // Solo la ficha bloquea la pantalla: si fallan los puestos, el error se ve en ese campo y lo demás sigue.
  const cargando = cargaEmpleado.cargando;
  const error = cargaEmpleado.error;
  const opcionesPuesto =
    puestoList.length > 0 ? puestoList : dataEmployee.puesto ? [dataEmployee.puesto] : [];

  useEffect(() => {
    getPuestos();
    getEmployee();
  }, [localReload]);

  const getPuestos = async () => {
    const result = await cargaPuestos.cargar();
    if (result) setPuestoList(result.data.result);
  };

  const getEmployee = async () => {
    const result = await cargaEmpleado.cargar();
    if (!result) {
      if (onFallo) onFallo();
      return;
    }

    setDataEmployee(result.data.result);
    setFirstName(result.data.result.firstName);
    setSecondName(result.data.result.secondName);
    setLastName(result.data.result.lastName);
    setSecondLastName(result.data.result.secondLastName);
    setSelectedPuesto(result.data.result.puesto.id);
    setIdentification(result.data.result.identification);
    setInssNumber(result.data.result.inssNumber);
    setAddress(result.data.result.address);
    setFechaInicio(new Date(result.data.result.fechaInicioContrato));
    setIsActive(result.data.result.isActive);
    setTipoContrato(result.data.result.tipoContrato);
    if (onDatos) {
      onDatos({
        fullName: result.data.result.fullName,
        puesto: result.data.result.puesto?.description,
        salario: result.data.result.salario,
        fechaInicioContrato: result.data.result.fechaInicioContrato,
        isActive: result.data.result.isActive,
      });
    }
  };

  //Devuelve un entero positivo
  const funtionSetINSS = (value) => {
    if (/^[0-9]+$/.test(value.toString()) || value === "") {
      setInssNumber(value);
      return;
    }
  };

  const saveChanges = () =>
    enviar(async () => {
      setIntentado(true);
      const fallos = validarFicha({
        firstName,
        lastName,
        identification,
        selectedPuesto,
        address,
      });
      if (Object.keys(fallos).length > 0) {
        llevarAlPrimerError(formulario);
        return;
      }

      const datos = {
        id,
        firstName,
        secondName,
        lastName,
        secondLastName,
        address,
        puestoId: selectedPuesto,
        inss: inssNumber,
        identification,
        tipoContrato,
        fechaInicioContrato: fechaInicio,
      };

      const ok = await escribir(
        () => updateEmployeeAsync(token, datos),
        "No se pudo actualizar al empleado"
      );
      if (!ok) return;
      aviso.exito("Empleado actualizado");
    });

  const texto = (campo, etiqueta, valor, alCambiar, extra = {}) => (
    <TextField
      fullWidth
      id={`${prefijo}-${campo}`}
      label={etiqueta}
      value={valor ?? ""}
      onChange={(e) => alCambiar(e.target.value)}
      error={Boolean(errores[campo])}
      helperText={errores[campo] || extra.ayuda || " "}
      disabled={!isEdit || guardando}
      inputProps={{ autoComplete: "off", ...extra.inputProps }}
      {...extra.props}
    />
  );

  let contenido;
  if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los datos del empleado"
        }
        onReintentar={() => setLocalReload(!localReload)}
      />
    );
  } else if (cargando) {
    contenido = <CargandoFormularioEmpleado etiqueta="Cargando los datos del empleado…" />;
  } else {
    contenido = (
      <Stack ref={formulario}>
        <SeccionFormulario titulo="Datos personales" plana>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              {texto(
                "firstName",
                "Primer nombre",
                firstName,
                (v) => setFirstName(v.toUpperCase()),
                { props: { required: true } }
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {texto("secondName", "Segundo nombre", secondName, (v) =>
                setSecondName(v.toUpperCase())
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {texto(
                "lastName",
                "Primer apellido",
                lastName,
                (v) => setLastName(v.toUpperCase()),
                { props: { required: true } }
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {texto("secondLastName", "Segundo apellido", secondLastName, (v) =>
                setSecondLastName(v.toUpperCase())
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {texto(
                "identification",
                "Cédula",
                identification,
                (v) => setIdentification(v.toUpperCase()),
                { ayuda: "Formato 000-000000-0000X", props: { required: true } }
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {texto(
                "address",
                "Dirección",
                address,
                (v) => setAddress(v.toLocaleUpperCase()),
                { props: { required: true } }
              )}
            </Grid>
          </Grid>
        </SeccionFormulario>

        <SeccionFormulario titulo="Contrato y puesto" plana>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                required
                fullWidth
                id={`${prefijo}-tipoContrato`}
                label="Tipo de contrato"
                value={tipoContrato}
                onChange={(e) => setTipoContrato(e.target.value)}
                helperText=" "
                disabled={!isEdit || guardando}
              >
                <MenuItem key={0} value={0}>
                  <em>Seleccione un tipo de contrato</em>
                </MenuItem>
                <MenuItem key={1} value={1}>
                  CONTRATO FIJO
                </MenuItem>
                <MenuItem key={2} value={2}>
                  SERVICIOS PROFESIONALES
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                required
                fullWidth
                id={`${prefijo}-selectedPuesto`}
                label="Puesto"
                value={selectedPuesto}
                onChange={(e) => setSelectedPuesto(e.target.value)}
                error={Boolean(errores.selectedPuesto)}
                helperText={
                  cargaPuestos.error ? (
                    <>
                      No se pudieron cargar los puestos.{" "}
                      <Button size="small" variant="text" onClick={getPuestos}>
                        Reintentar
                      </Button>
                    </>
                  ) : cargaPuestos.cargando ? (
                    "Cargando los puestos…"
                  ) : (
                    errores.selectedPuesto || " "
                  )
                }
                disabled={!isEdit || guardando}
              >
                <MenuItem value="">
                  <em>Seleccione un puesto</em>
                </MenuItem>
                {opcionesPuesto.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.description}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              {texto("inssNumber", "Número INSS", inssNumber, funtionSetINSS, {
                inputProps: { inputMode: "numeric" },
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              <CampoFecha
                label="Fecha de ingreso"
                value={fechaInicio}
                onChange={(newValue) => setFechaInicio(newValue)}
                disabled={!isEdit || guardando}
              />
            </Grid>
          </Grid>
        </SeccionFormulario>

        {isEdit ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              size="large"
              disabled={guardando}
              onClick={() => saveChanges()}
              startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 220 }}
            >
              {guardando ? "Guardando…" : "Guardar cambios"}
            </Button>
          </Box>
        ) : null}
      </Stack>
    );
  }

  return (
    <div>
      {/* Las acciones del empleado, a la vista: antes estaban escondidas en un menú de tres puntos. */}
      {cargando || error ? null : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          {isActive ? (
            <>
              {puedeEditar ? (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => {
                      setIsEdit(!isEdit);
                    }}
                    aria-pressed={isEdit}
                  >
                    {isEdit ? "Dejar de editar" : "Editar"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PaidOutlinedIcon />}
                    onClick={() => setEditSalario(true)}
                  >
                    Cambiar salario
                  </Button>
                </>
              ) : null}
              {puedeLiquidar ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PersonOffOutlinedIcon />}
                  onClick={() => setModalLiaquidar(true)}
                >
                  Liquidar
                </Button>
              ) : null}
            </>
          ) : (
            <Button
              variant="outlined"
              startIcon={<PrintOutlinedIcon />}
              onClick={() => setModalLiaquidarPrint(true)}
            >
              Imprimir liquidación
            </Button>
          )}
        </Box>
      )}

      {contenido}

      {showEditSalario ? (
        <ChangeSalario
          id={id}
          salarioActual={dataEmployee.salario}
          setShowModal={setEditSalario}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      <DialogoFormulario
        abierto={showModalLiaquidar}
        titulo="Liquidar empleado"
        ancho="md"
        sinAcciones
        hayCambios={liquidacionEscrita}
        onCerrar={() => {
          setLiquidacionEscrita(false);
          setModalLiaquidar(false);
        }}
      >
        {showModalLiaquidar ? (
          <LiquidateEmployee
            dataEmployee={dataEmployee}
            onBonoEscrito={setLiquidacionEscrita}
            setShowModal={setModalLiaquidar}
            reload={localReload}
            setReload={setLocalReload}
          />
        ) : null}
      </DialogoFormulario>

      <MediumModal
        titulo={"Liquidacion de Prestaciones Sociales"}
        isVisible={showModalLiaquidarPrint}
        setVisible={setModalLiaquidarPrint}
      >
        <PrintLiquidacion empleadoId={id} />
      </MediumModal>
    </div>
  );
};

export default EmployeeDetails;
